import { z } from 'zod';
export const insightsExportFieldSchema = z.object({
    key: z.string(),
    label: z.string(),
    visibility: z.enum(['dashboard', 'exportOnly']),
    availabilityNote: z.string().optional(),
});
export const insightsExportSheetSchema = z.object({
    name: z.string(),
    fields: z.array(insightsExportFieldSchema),
});
export const insightsExportManifestSchema = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string(),
    detailLevel: z.enum(['dashboardSummary', 'addsExportDetail']),
    sheets: z.array(insightsExportSheetSchema),
});
const field = (label, visibility = 'exportOnly') => ({
    key: label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    label,
    visibility,
});
const sheet = (name, dashboardLabels, exportOnlyLabels) => ({
    name,
    fields: [
        ...dashboardLabels.map((label) => field(label, 'dashboard')),
        ...exportOnlyLabels.map((label) => field(label, 'exportOnly')),
    ],
});
const ESCALATION_CLASSIFICATION_AVAILABILITY_NOTE = 'Available when escalation reason reporting is enabled; raw Topics remain in every export';
const escalationSheet = () => {
    const dashboardLabels = new Set([
        'Reference',
        'Resident Name',
        'Portfolio',
        'Risk Level',
        'Category',
        'Subcategory',
        'Classification Status',
        'Raw Topic',
        'Response Status',
    ]);
    const classificationLabels = new Set([
        'Category',
        'Subcategory',
        'Classification Status',
        'Taxonomy Version',
    ]);
    const exportLabels = [
        'Reference',
        'Resident Name',
        'Portfolio',
        'Building',
        'Unit',
        'Risk Level',
        'Category',
        'Subcategory',
        'Classification Status',
        'Taxonomy Version',
        'Raw Topic',
        'Escalated At',
        'Response Status',
        'Reply Sender Emails',
        'Reply Sender Names',
        'Reply Count',
        'Missing Reply Sender Count',
        'First Reply Sender Email',
        'First Reply Sender Name',
        'Reply Time (minutes)',
        'Wait Time (minutes)',
        'Subject line',
        'Reply To',
        'CC',
    ];
    return {
        name: 'Escalations',
        fields: exportLabels.map((label) => ({
            ...field(label, dashboardLabels.has(label) ? 'dashboard' : 'exportOnly'),
            ...(classificationLabels.has(label)
                ? {
                    availabilityNote: ESCALATION_CLASSIFICATION_AVAILABILITY_NOTE,
                }
                : {}),
        })),
    };
};
export const INSIGHTS_EXPORT_MANIFESTS = {
    reviews: {
        key: 'reviews',
        label: 'Reviews',
        description: 'Exports the review records behind the Reviews dashboard.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Reviews', ['Portfolio', 'Reviewer', 'Review Text', 'Date', 'Rating'], ['Owner Reply', 'Review Link']),
        ],
    },
    reviewDiscovery: {
        key: 'reviewDiscovery',
        label: 'Review Discovery',
        description: 'Exports review records matching the discovery search and filters.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Review Search Results', ['Portfolio', 'Reviewer', 'Review Text', 'Date', 'Rating'], ['Owner Reply', 'Review Link']),
        ],
    },
    recertification: {
        key: 'recertification',
        label: 'Recertification',
        description: 'Exports resident-level recertification outreach and response status.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Recertification', ['Portfolio', 'Status', 'Response Count', 'Conversion Status'], ['First Name', 'Last Name', 'Apartment', 'Due Date']),
        ],
    },
    serviceRequests: {
        key: 'serviceRequests',
        label: 'Service Requests',
        description: 'Exports service request survey responses and completion details.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Service Requests', ['Portfolio', 'Service Type', 'Problem', 'Days to Complete', 'Response', 'Sentiment'], ['Unit', 'Technician', 'Opened Date', 'Closed Date', 'Survey Date']),
        ],
    },
    escalations: {
        key: 'escalations',
        label: 'Escalations',
        description: 'Exports escalation records with counted reply attribution and timing fields.',
        detailLevel: 'addsExportDetail',
        sheets: [escalationSheet()],
    },
    moveInSurveys: {
        key: 'moveInSurveys',
        label: 'Move-in and welcome surveys',
        description: 'Exports survey responses with resident contact, timing, and work-order context.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Move-In and Welcome Surveys', ['Portfolio', 'Survey Type', 'Survey Question', 'Resident Response', 'Response Sentiment'], [
                'Resident First Name',
                'Resident Last Name',
                'Resident Email',
                'Resident Phone',
                'Building Address',
                'Unit',
                'Move-In Date',
                'Survey Sent At',
                'Sentiment Score',
                'Response Time (min)',
                'Move-In Work Orders',
                'Work Order Types',
            ]),
        ],
    },
    quarterlyCheckins: {
        key: 'quarterlyCheckins',
        label: 'Quarterly check-ins',
        description: 'Exports quarterly check-in responses with resident contact and response metadata.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Quarterly Check-In Surveys', ['Portfolio', 'Survey Question', 'Resident Response', 'Response Sentiment'], [
                'Resident First Name',
                'Resident Last Name',
                'Resident Email',
                'Resident Phone',
                'Building Address',
                'Unit',
                'Survey Sent At',
                'Sentiment Score',
                'Response Time (min)',
            ]),
        ],
    },
    unitConditionChecks: {
        key: 'unitConditionChecks',
        label: 'Unit Condition Check Status Report',
        description: 'Exports unit-level outreach status. Conversation message content is not included.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Unit Condition Checks', [
                'Portfolio',
                'Unit',
                'Reply Status',
                'Replied',
                'Positive Responses',
                'Negative Responses',
            ], [
                'Street Number',
                'Street Name',
                'First Sent At',
                'Messages Sent',
                'Escalations',
                'Yes Count',
                'No Count',
            ]),
        ],
    },
    renewals: {
        key: 'renewals',
        label: 'Renewals',
        description: 'Exports renewal survey responses with resident intent and renewal status.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Renewals', ['Portfolio Name', 'Unit', 'Resident Name', 'Lease Expiry', 'Intent', 'Renewal Status'], ['Survey Date', 'Response Date', 'Has Response', 'Response Text', 'Sentiment']),
        ],
    },
    leasingProspects: {
        key: 'leasingProspects',
        label: 'Leasing Prospects',
        description: 'Exports prospect-level leasing activity for the selected lead cohort.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Prospects', ['Portfolio', 'Status', 'Lead Source', 'Tour Date', 'Message Count'], ['First Name', 'Last Name', 'Email', 'Phone', 'Tour Time', 'Last Activity']),
        ],
    },
    leadSourcePerformance: {
        key: 'leadSourcePerformance',
        label: 'Lead Source Performance',
        description: 'Exports the lead-source funnel table shown on the dashboard.',
        detailLevel: 'dashboardSummary',
        sheets: [
            sheet('Lead Source Performance', [
                'Lead Source',
                'Leads',
                'Consent',
                'L→C %',
                'Tours',
                'C→T %',
                'Apps',
                'T→A %',
                'Leases',
                'A→L %',
                'Overall %',
            ], []),
        ],
    },
    surveyOutreachSettings: {
        key: 'surveyOutreachSettings',
        label: 'Survey Outreach Settings',
        description: 'Exports the outreach settings and touchpoint configuration shown in the dashboard.',
        detailLevel: 'dashboardSummary',
        sheets: [
            sheet('Outreach Settings', [
                'Portfolio',
                'Account',
                'Outreach Type',
                'Status',
                'Priority',
                'Schedule Summary',
                'When It Goes Out',
                'Touchpoint',
                'Message',
                'Auto Response',
                'Channel',
            ], []),
        ],
    },
    portfolioComparison: {
        key: 'portfolioComparison',
        label: 'Portfolio Comparison',
        description: 'Exports the portfolio comparison table shown in the dashboard.',
        detailLevel: 'dashboardSummary',
        sheets: [sheet('Portfolio Comparison', ['Portfolio', 'Metric', 'Value', 'Rank', 'Trend'], [])],
    },
    businessReview: {
        key: 'businessReview',
        label: 'Business Review',
        description: 'Exports the business review summary, highlights, and property breakdown.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Executive Summary', ['Metric', 'Value'], []),
            sheet('Highlights', ['Highlight', 'Property', 'Value'], []),
            sheet('Property Breakdown', ['Property', 'Units', 'Engagement %', 'Handled %', 'Collected $', 'Time Saved'], [
                'Engaged Residents',
                'Residents Escalated to Human',
                'Review Lift %',
                'Pre-Launch 5-Star/mo',
                'Post-Launch 5-Star/mo',
                'Renewal Response %',
                'Maintenance Satisfaction %',
                'Review Links Sent',
                'Review Clicks',
                'Review Click Rate %',
                'Renewal Follow-Ups',
                'Renewal Responses',
                'Completed Renewals',
            ]),
        ],
    },
    businessReviewSlides: {
        key: 'businessReviewSlides',
        label: 'Business Review Slides',
        description: 'Creates a Google Slides deck from the business review narrative and property examples.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Slides', ['Resident Engagement', 'Operations', 'Collections', 'Renewals', 'Review Lift'], [
                'Executive Summary Slides',
                'Property Examples',
                'Speaker-Ready Narrative',
                'Google Slides Link',
            ]),
        ],
    },
    businessReviewRoiWorkbook: {
        key: 'businessReviewRoiWorkbook',
        label: 'Business Review ROI Excel',
        description: 'Exports the ROI value model as a workbook for filtering, sorting, and reviewing assumptions.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('ROI Workbook', [
                'Annual ResiDesk Cost',
                'Renewal Value',
                'Maintenance Value',
                'Collections Value',
                'Support Value',
            ], [
                'ROI Assumptions',
                'Property Breakdown',
                'Savings by Category',
                'Payback Period',
                'Net Value',
            ]),
        ],
    },
    businessReviewRoiSlides: {
        key: 'businessReviewRoiSlides',
        label: 'Business Review ROI Slides',
        description: 'Creates a Google Slides deck from the ROI value model and assumption set.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('ROI Slides', [
                'Annual ResiDesk Cost',
                'Renewal Value',
                'Maintenance Value',
                'Collections Value',
                'Support Value',
            ], [
                'ROI Assumptions',
                'Value Category Slides',
                'Presentation-Ready Summary',
                'Google Slides Link',
            ]),
        ],
    },
    businessReviewRoiPdf: {
        key: 'businessReviewRoiPdf',
        label: 'Business Review ROI PDF',
        description: 'Exports the ROI value model as a formatted PDF summary for sharing.',
        detailLevel: 'dashboardSummary',
        sheets: [
            sheet('ROI PDF', [
                'Annual ResiDesk Cost',
                'Renewal Value',
                'Maintenance Value',
                'Collections Value',
                'Support Value',
            ], []),
        ],
    },
    optOutResidents: {
        key: 'optOutResidents',
        label: 'Opt-out Residents',
        description: 'Exports the resident list behind the opt-out rate summary.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Opt-out Residents', ['Opt-Out Rate', 'Opted-Out Resident Count', 'Reachable Resident Count'], ['Portfolio', 'Resident Name', 'Unit', 'Phone', 'Opted Out At']),
        ],
    },
    delinquencyPortfolioSummary: {
        key: 'delinquencyPortfolioSummary',
        label: 'Delinquency Portfolio Summary',
        description: 'Exports portfolio-level delinquency balances and AI reminder outcomes.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Portfolio Summary', [
                'Portfolio',
                'Active Residents',
                'Active Delinquent Balance',
                'Active Delinquent Residents',
                'Active Delinquency Rate (%)',
            ], [
                'Former/Unmatched Balance',
                'Raw Collections Balance',
                'Payments Received',
                'Paydown Rate (%)',
                'AI Reminders Sent',
                'AI Response Rate (%)',
                'Bad Debt',
            ]),
        ],
    },
    delinquencyResidentDetail: {
        key: 'delinquencyResidentDetail',
        label: 'Delinquency Resident Detail',
        description: 'Exports resident-level delinquency details for collections review.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Resident Detail', ['Resident Name', 'Portfolio', 'Unit', 'Balance', 'Age Bucket'], [
                'Days Delinquent',
                'Last Payment Date',
                'Last Payment Amount',
                'AI Reminders Sent',
                'Reminder Status',
                'Promised Date',
                'Legal Status',
            ]),
        ],
    },
    delinquencyFullDashboard: {
        key: 'delinquencyFullDashboard',
        label: 'Delinquency Full Dashboard',
        description: 'Exports dashboard summary sheets plus collections, AI impact, and resident detail.',
        detailLevel: 'addsExportDetail',
        sheets: [
            sheet('Dashboard Summary', ['Metric', 'Value'], []),
            sheet('Active Resident Cohort', ['Metric', 'Value'], []),
            sheet('Collections by Age', ['Age Bucket', 'Amount', 'Resident Count'], []),
            sheet('AI Impact', ['Metric', 'Value'], []),
            sheet('Resident Detail', ['Resident Name', 'Portfolio', 'Unit', 'Balance', 'Age Bucket'], [
                'Days Delinquent',
                'Last Payment Date',
                'Last Payment Amount',
                'AI Reminders Sent',
                'Reminder Status',
                'Promised Date',
                'Legal Status',
            ]),
        ],
    },
};
export function getInsightsExportManifest(key) {
    if (!key)
        return null;
    return INSIGHTS_EXPORT_MANIFESTS[key] || null;
}
export function getInsightsExportHeaders(key, sheetName) {
    const manifest = getInsightsExportManifest(key);
    if (!manifest)
        return [];
    const sheets = sheetName
        ? manifest.sheets.filter((currentSheet) => currentSheet.name === sheetName)
        : manifest.sheets;
    return sheets.flatMap((currentSheet) => currentSheet.fields.map((currentField) => currentField.label));
}
const formatExportFieldLabel = (currentField) => currentField.availabilityNote
    ? `${currentField.label} (${currentField.availabilityNote})`
    : currentField.label;
export function getInsightsExportContentSummary(key) {
    const manifest = getInsightsExportManifest(key);
    if (!manifest)
        return null;
    const dashboardLabels = manifest.sheets.flatMap((currentSheet) => currentSheet.fields
        .filter((currentField) => currentField.visibility === 'dashboard')
        .map(formatExportFieldLabel));
    const exportOnlyLabels = manifest.sheets.flatMap((currentSheet) => currentSheet.fields
        .filter((currentField) => currentField.visibility === 'exportOnly')
        .map(formatExportFieldLabel));
    return {
        manifest,
        dashboardItems: [...new Set(dashboardLabels)],
        exportItems: [...new Set(exportOnlyLabels)],
    };
}
